import React, { useCallback, useState } from 'react';
import { v4 as uuid } from 'uuid';

import { useQueryFilters } from '@hooks/useQueryFilters';
import { store } from '@store';
import { translate } from '@chaika/i18n/helper';
import { assertIsDefined } from '@utils/assertIsDefined';

import { useLogin, useRecoverPassword, useCreateRecoveryToken } from '../queries/authMutations';
import { useAuthErrors } from './useAuthErrors';
import { BrowserAutofillValues } from './useAuthPageBootstrap';

interface QueryParams {
    email?: string;
    token?: string;
}

interface UseAuthFormReturn {
    email: string;
    password: string;
    confirmPassword: string;
    passwordRecoveryMode: boolean;
    errorMessage?: string;
    userErrorMessage?: string;
    passwordErrorMessage?: string;
    isBrowserAutofillCheckNeeded: boolean;
    applyBrowserAutofill(values: BrowserAutofillValues): void;
    onInputChange(event: React.ChangeEvent<HTMLInputElement>): void;
    onLoginClick(): Promise<void>;
    onRecoveryClick(): Promise<void>;
    onBackButtonClick(): void;
}

export function useAuthForm(): UseAuthFormReturn {
    const { getQueryFilterParams } = useQueryFilters<QueryParams>();
    const [initialQueryParams] = useState(() => getQueryFilterParams());
    const hasRecoveryParams = !!initialQueryParams.email && !!initialQueryParams.token;
    const token = hasRecoveryParams ? initialQueryParams.token : undefined;
    const recoveryEmail = hasRecoveryParams ? initialQueryParams.email : undefined;

    const [email, setEmail] = useState(recoveryEmail ?? '');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordRecoveryMode, setPasswordRecoveryMode] = useState(hasRecoveryParams);

    const {
        errorMessage,
        userErrorMessage,
        passwordErrorMessage,
        setFromError: setErrorMessageFromError,
        setUserErrorMessage,
        setPasswordErrorMessage,
        reset: resetErrorMessages,
    } = useAuthErrors();

    const { mutateAsync: login } = useLogin();
    const { mutateAsync: recoverPassword } = useRecoverPassword();
    const { mutateAsync: createRecoveryToken } = useCreateRecoveryToken();

    const applyBrowserAutofill = useCallback((values: BrowserAutofillValues) => {
        if (values.email) {
            setEmail(values.email);
        }
        if (values.password) {
            setPassword(values.password);
        }
    }, []);

    function onInputChange({ target: { value, name } }: React.ChangeEvent<HTMLInputElement>): void {
        if (name === 'email') {
            setEmail(value);
        } else if (name === 'password') {
            setPassword(value);
        } else if (name === 'confirmPassword') {
            setConfirmPassword(value);
        }
    }

    function validate(): boolean {
        if (!passwordRecoveryMode && email.length <= 1) {
            setUserErrorMessage(translate('checkEmail'));
            return false;
        }
        if (password.length < 6) {
            setPasswordErrorMessage(translate('passwordTooShort'));
            return false;
        }
        if (passwordRecoveryMode && password !== confirmPassword) {
            setPasswordErrorMessage(translate('passwordsDoNotMatch'));
            return false;
        }

        return true;
    }

    async function onLoginClick(): Promise<void> {
        resetErrorMessages();

        if (!validate()) {
            return;
        }

        store.app.isOnUpdate = true;
        if (passwordRecoveryMode) {
            await processPasswordRecovery();
        } else {
            await processLogin();
        }
        store.app.isOnUpdate = false;
    }

    async function onRecoveryClick(): Promise<void> {
        resetErrorMessages();

        if (email.length > 1) {
            store.app.isOnUpdate = true;
            try {
                await createRecoveryToken({ email, id: uuid() });

                store.app.showPopup({
                    title: translate('restorePasswordSecond'),
                    description: `${translate(`restoreLinkHasBeenSentOnEmail`)} - ${email}`,
                });
            } catch (e: any) {
                setErrorMessageFromError(e);
                console.error('e', e);
            }
            store.app.isOnUpdate = false;
        }
    }

    function onBackButtonClick(): void {
        setPasswordRecoveryMode(false);
    }

    async function processLogin(): Promise<void> {
        try {
            await login({ email, password });
        } catch (e: any) {
            setErrorMessageFromError(e);
            console.error('e', e);
        }
    }

    async function processPasswordRecovery(): Promise<void> {
        try {
            assertIsDefined(token, 'Password recovery mode is enabled only by a recovery link with a token');
            await recoverPassword({ token, recovery: { email, password } });
        } catch (e: any) {
            setErrorMessageFromError(e);
            console.error('e', e);
        }

        setPasswordRecoveryMode(false);
    }

    return {
        email,
        password,
        confirmPassword,
        passwordRecoveryMode,
        errorMessage,
        userErrorMessage,
        passwordErrorMessage,
        isBrowserAutofillCheckNeeded: !initialQueryParams.email,
        applyBrowserAutofill,
        onInputChange,
        onLoginClick,
        onRecoveryClick,
        onBackButtonClick,
    };
}
